import React, { useEffect, useState } from "react";
import MainLayout from "../components/Layouts/MainLayout";
import apiClient from "../api/apiClient";
import { useSelector } from "react-redux";
import FeedbacksList from "../components/Feedbacks/FeedbacksList";

function Feedbacks() {
  const [feedbackAvg, setFeedbackAvg] = useState(null);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [ratingDistribution, setRatingDistribution] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { token, user } = useSelector((state) => state.auth);

  // Fetch feedback metrics
  const getFeedbackMetrics = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/api/dashboard/avg-feedback/`);
      setFeedbackAvg(response.data.average);
      setTotalFeedbacks(response.data.total_feedbacks || 0);
      setRatingDistribution(response.data.distribution || null);
    } catch (error) {
      console.error("Error fetching feedback metrics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Time of day greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // Fire the request on component mount
  useEffect(() => {
    getFeedbackMetrics();
  }, []);

  // Map rating to star display
  const getRatingStars = (rating) => {
    if (rating === null) return null;
    
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    return (
      <div className="flex items-center mt-2">
        {[...Array(5)].map((_, i) => (
          <svg 
            key={i}
            className={`w-6 h-6 ${i < fullStars ? 'text-yellow-400' : (i === fullStars && hasHalfStar ? 'text-yellow-400' : 'text-gray-300')}`} 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            {i < fullStars || (i === fullStars && hasHalfStar) ? (
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
            ) : (
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
            )}
          </svg>
        ))}
        <span className="ml-2 text-sm font-medium text-gray-600 dark:text-gray-300">
          {rating !== null ? rating.toFixed(1) : 'N/A'}
        </span>
      </div>
    );
  };

  return (
    <MainLayout>
      <main className="p-4 md:ml-64 min-h-screen pt-10 bg-gradient-to-br  dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto sm:px-6 py-10 mb-5">
          {/* Header Section with Animated Greeting */}
          <div className="px-4 sm:px-0 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <span className="inline-block px-3 py-1 text-sm font-medium text-emerald-800 bg-emerald-100 rounded-full dark:bg-emerald-900 dark:text-emerald-200">
                  {getGreeting()}
                </span>
                <h1 className="mt-2 text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white">
                  Hello, <span className="text-emerald-600 dark:text-emerald-400">{user?.first_name}</span>!
                </h1>
                <p className="mt-1 text-gray-600 dark:text-gray-300">
                  Your feedback dashboard overview
                </p>
              </div>
      
            </div>
          </div>

          {/* Stats Section */}
          <div className="px-4 sm:px-0 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Feedback Stats Card */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">Feedback Overview</h2>
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                      All Time
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Average Rating Card */}
                    <div className="rounded-lg p-4 dark:from-gray-700 dark:to-gray-700 relative overflow-hidden">
                      <div className="absolute right-0 top-0 w-20 h-20 opacity-10">
                        <svg className="text-emerald-600 dark:text-emerald-400 w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Average Rating
                      </h3>
                      {isLoading ? (
                        <div className="animate-pulse h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                      ) : (
                        <div className="flex flex-col">
                          <span className="text-3xl font-bold text-gray-900 dark:text-white">
                            {feedbackAvg !== null ? feedbackAvg.toFixed(1) : 'N/A'}
                          </span>
                          {getRatingStars(feedbackAvg)}
                        </div>
                      )}
                    </div>

                    {/* Total Feedbacks Card */}
                    <div className="rounded-lg p-4 dark:from-gray-700 dark:to-gray-700 relative overflow-hidden">
                      <div className="absolute right-0 top-0 w-20 h-20 opacity-10">
                        <svg className="text-cyan-600 dark:text-cyan-400 w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd"></path>
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                        Total Feedbacks
                      </h3>
                      {isLoading ? (
                        <div className="animate-pulse h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                      ) : (
                        <div className="flex items-end">
                          <span className="text-3xl font-bold text-gray-900 dark:text-white">{totalFeedbacks}</span>
                          <div className="ml-2 text-sm font-medium text-cyan-600 dark:text-cyan-400">
                           
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Rating Distribution Card */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">Rating Distribution</h2>
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200">
                      Performance Metrics
                    </span>
                  </div>

                  {isLoading ? (
                    <div className="space-y-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="animate-pulse flex items-center w-full">
                          <div className="w-10 h-4 bg-gray-200 dark:bg-gray-600 rounded mr-2"></div>
                          <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[5, 4, 3, 2, 1].map((rating) => {
                        // Get actual data from API response
                        const ratingData = ratingDistribution?.[rating.toString()];
                        const count = ratingData?.count || 0;
                        const percentage = ratingData?.percentage || 0;
                        
                        let barColor;
                        if (rating >= 4) barColor = "bg-green-500";
                        else if (rating >= 3) barColor = "bg-yellow-400";
                        else barColor = "bg-red-500";
                        
                        return (
                          <div key={rating} className="flex items-center">
                            <div className="flex items-center w-12">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{rating}</span>
                              <svg className="w-4 h-4 text-yellow-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                              </svg>
                            </div>
                            <div className="w-full ml-4">
                              <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                                <div className={`${barColor} h-2 rounded-full transition-all duration-300`} style={{ width: `${percentage}%` }}></div>
                              </div>
                            </div>
                            <div className="flex items-center w-20 text-right">
                              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {percentage.toFixed(0)}% ({count})
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Feedbacks List Section */}
          <div className="px-4 sm:px-0">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-bold text-gray-800 dark:text-white">Recent Feedbacks</h2>
                  <div>
                    {/* <button className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-gradient-to-r from-cyan-500 to-emerald-500 text-white hover:from-cyan-600 hover:to-emerald-600 transition-all duration-200">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                      </svg>
                      Filter
                    </button> */}
                  </div>
                </div>
                
                {/* FeedbacksList Component */}
                <FeedbacksList />
              </div>
            </div>
          </div>
        </div>
      </main>
    </MainLayout>
  );
}

export default Feedbacks;